/**
 * File types a learner can add to Study. The server allow-list is `studyMimeSchema` in
 * platform-backend `src/study/study.dto.ts`; keep both lists the same.
 */

/** Photo types. A GIF is read from its first frame only. */
export const STUDY_PHOTO_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/gif',
] as const;

export const STUDY_PPTX_MIME =
  'application/vnd.openxmlformats-officedocument.presentationml.presentation';
export const isStudyPresentation = (file: {
  type?: string;
  mime_type?: string;
  name?: string;
  file_name?: string;
}) =>
  file.type === STUDY_PPTX_MIME ||
  file.mime_type === STUDY_PPTX_MIME ||
  /\.pptx$/i.test(file.name ?? file.file_name ?? '');
export const STUDY_UPLOAD_TYPES = [
  'application/pdf',
  STUDY_PPTX_MIME,
  ...STUDY_PHOTO_TYPES,
] as const;

/** `accept` value for a photo-only input (camera or gallery). */
export const STUDY_PHOTO_ACCEPT = STUDY_PHOTO_TYPES.join(',');
/** `accept` value for a file input that takes a PDF or a photo. */
export const STUDY_UPLOAD_ACCEPT = STUDY_UPLOAD_TYPES.join(',');

export const STUDY_UNSUPPORTED_FILE_MESSAGE =
  'Use a photo, PDF or PPTX. Older .ppt files, charts and embedded objects should be exported to PDF first.';

/** Browser MIME may be absent for Office files. The server still verifies the actual bytes. */
export function studyUploadMime(file: { type: string; name?: string }): string {
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  if (/\.pptx$/i.test(file.name ?? '')) return STUDY_PPTX_MIME;
  if (/\.pdf$/i.test(file.name ?? '')) return 'application/pdf';
  return file.type;
}

/** Capture handles one paginated file or several photos, never silently discards a selection. */
export function captureSelectionError(
  files: Array<{ type: string; name?: string }>,
): string | null {
  if (!files.length) return 'Choose a photo, PDF or PPTX to continue.';
  if (
    files.length > 1 &&
    files.some((file) => ['application/pdf', STUDY_PPTX_MIME].includes(studyUploadMime(file)))
  )
    return 'Choose one PDF or PPTX at a time, or choose several photos. No files have been uploaded.';
  return null;
}

export function isSupportedStudyFile(file: { type: string; name?: string }): boolean {
  return (STUDY_UPLOAD_TYPES as readonly string[]).includes(studyUploadMime(file));
}

/** Thrown before any upload starts, so the learner never sees a raw server error. */
export class StudyUnsupportedFileError extends Error {
  readonly code = 'STUDY_FILE_TYPE_UNSUPPORTED';
  constructor() {
    super(STUDY_UNSUPPORTED_FILE_MESSAGE);
  }
}

/** Throws `StudyUnsupportedFileError` for a file type the server would refuse. */
export function assertSupportedStudyFile(file: { type: string; name?: string }): void {
  if (!isSupportedStudyFile(file)) throw new StudyUnsupportedFileError();
}
